import {
  type AccountContactsEdge,
  type AchAccountOwnershipType,
  type BankAccountType,
  type PositionObjectType,
} from "common/types/federation/supergraphTypes";
import { type Maybe } from "graphql/jsutils/Maybe";
import { type InferType } from "yup";
import { type RecentlyAddedContactType } from "apps/portal/Portal/common/Settings/InvestmentAccounts/AddContact/types";
import {
  type achConfirmValidation,
  type achUpdateValidation,
  type checkUpdateValidation,
  type existingPaymentValidation,
  type wireUpdateValidation,
} from "./utils";

export interface PaymentPref {
  id?: Maybe<string>;
  paymentMethod?: Maybe<string>;
  bankInfoRedacted?: Maybe<string>;
  address?: Address;
  checkMailingAddressSameAsAccount?: Maybe<boolean>;
}

export interface ExistingPaymentPref extends PaymentPref {
  id: string;
}

export interface Address {
  street?: string | null;
  street2?: string | null;
  street3?: string | null;
  city?: string | null;
  state?: string | null;
  region?: string | null;
  country?: string | null;
  postalCode?: string | null;
  countryId?: string | null;
  stateId?: string | null;
  streetLengthError?: string | null;
  cityStatePostalLengthError?: string | null;
}
export interface AddressUpdateArgs extends Address {
  updateCheckAddress: string;
}
export type AddressFieldNames = Omit<Address, "state" | "country">;

type WireFormValues = InferType<ReturnType<typeof wireUpdateValidation>>;
type CheckFormValues = InferType<ReturnType<typeof checkUpdateValidation>>;
export interface AchUpdateFormValues extends Omit<
  InferType<typeof achUpdateValidation>,
  "accountOwnership" | "accountType"
> {
  accountOwnership: AchAccountOwnershipType;
  accountType: BankAccountType;
}
type AchConfirmFormValues = InferType<typeof achConfirmValidation>;
type ExistingPaymentFormValues = InferType<typeof existingPaymentValidation>;

export interface PaymentPrefFormValues
  extends
    WireFormValues,
    ExistingPaymentFormValues,
    CheckFormValues,
    AchUpdateFormValues,
    AchConfirmFormValues {}

export interface ChangedPosition {
  id: string;
  __typename?: "PositionObjectType" | undefined;
  account: {
    __typename?: "AccountObjectType" | undefined;
    id: string;
  } | null;
  investmentEntity: {
    __typename?: "AccountObjectType" | undefined;
    name: string;
  };
}

export enum InvestmentAccountActions {
  UpdatePayment = "UpdatePayment",
  UpdateAddress = "UpdateAddress",
  UpdateContactCommunicationPreferences = "UpdateContactCommunicationPreferences",
  ManageContacts = "ManageContacts",
  RemoveContacts = "RemoveContacts",
  AddContact = "AddContact",
}

export type Position = PositionObjectType;

export interface GroupedContact {
  id: string;
  displayNameArray: string[];
  emailAddress: string;
  presentInAccounts: string[];
  associatedContacts: AccountContactsEdge[] | RecentlyAddedContactType[];
}

export type AccountContactMap = {
  accountId?: string;
  accountContactId: string;
  accountName: string;
  arenaId: string;
  arenaName: string;
  communicationPrefs: {
    emailDistributionList?: EmailDistribution;
  }[];
  contactDetails: {
    portalFullName: string;
    primaryEmail: {
      emailAddress: string;
    };
  };
  contactId?: string;
  isAdminContact: boolean;
  isMainContact: boolean;
  label?: {
    labelText: string;
  };
};

export type UpdateContactPayloadObject = {
  arena_id: number;
  arena_name: string;
  account_id: string;
  account_name: string;
  account_contact_id: string;
  contact_id: string;
  contact_name: string;
  label: string | null;
  is_main_contact: boolean;
  is_admin_contact: boolean;
  distribution_list: string[];
  object_type?: string;
};

export type UpdateContactChangeRequestPayload = {
  requestPayload: UpdateContactPayloadObject;
};

export type EmailDistribution = {
  id: string;
  name: string;
  description?: string;
};

export type RemoveContactsChangeRequestPayload = {
  requestPayload: {
    arena_id: number;
    arena_name: string;
    account_id: string;
    account_name: string;
    account_contact_id: string;
    contact_name: string;
    contact_id: string;
    object_type?: string;
  };
};

export type UpdateStateObject = {
  stateName?: ContactActionState;
  nextStateToLoad: ContactActionState;
  stateData?: object;
};

export enum ContactActionState {
  Loading = "Loading",
  SelectContact = "SelectContact",
  AddContact = "AddContact",
  ContactDetails = "ContactDetails",
  UpdateContact = "UpdateContact",
  Submitting = "Submitting",
  Completed = "Completed",
  Failed = "Failed",
}

export enum ObjectType {
  Account = "Account",
}
